/**
 * /dashboard/leave/calendar — Leave → Team calendar (SOP §1.2;
 * Phase 3b — 2026-06-01).
 *
 * Distinct from /dashboard/calendar (the personal floor surface that
 * shows the caller's own leaves + holidays overlaid on a month grid):
 * this is the TEAM-oriented view promised in the SOP. v1 is a stub —
 * the rich interactive team calendar is on the roadmap; today it links
 * to the public holidays page so an HR/manager has a starting point.
 *
 * Migration note: port of
 * frontend/src/app/(dashboard)/dashboard/leave/calendar/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - next/link → @tanstack/react-router Link (typed)
 * The /dashboard/holidays link is the existing public-holiday admin
 * surface; it lands in a later Phase 3 slice (HR admin module). The
 * link is currently a runtime-string `to` so the strict typed-route
 * check accepts it until the holidays route is registered (same
 * pattern Phase 3a used for forward references).
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CalendarDays } from "lucide-react";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { LeaveFeatureGate } from "@/components/leave/leave-feature-gate";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/leave/calendar",
)({
  component: LeaveTeamCalendarRoute,
});

// Holidays admin page (HR Core module). Lands in a later Phase 3 slice;
// declared as runtime `string` so the strict typed-Link check accepts
// it until then.
const HOLIDAYS_HREF: string = "/app/$tenantSlug/holidays";

function LeaveTeamCalendarRoute() {
  return (
    <TimeAndLeaveShell
      title="Team calendar"
      description="Who's out, when — across your team and the organisation."
    >
      <LeaveFeatureGate>
        <div className="space-y-4">
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarDays className="h-4 w-4 text-primary" />
                Team calendar
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                The interactive team calendar (who&apos;s out when) is on
                the roadmap. Today you can see public holidays for your
                organisation on the dedicated calendar page.
              </p>
              <Button asChild variant="outline" className="gap-1.5">
                <Link to={HOLIDAYS_HREF}>
                  Open public holidays
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </LeaveFeatureGate>
    </TimeAndLeaveShell>
  );
}
